import React, { useEffect, useState } from 'react';
import { formatSecs, getIssueProject, paginatedFetch, iAxios } from '../../lib/utils.js';
import moment from 'moment';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { alertMessage } from '../../lib/actions.js';
import { useProjectNames } from '../../lib/hooks.js';
import { ModalManager } from '../common/Helper.js';
import Icon from '../ds/Icon.js';

const RelatedBlock = ({ item }) => (
    <div className="issue-related">
        <a href={item.url}>
            {item.title}
            <br />
            <span>S {formatSecs(item.timeSpent)} / E {formatSecs(item.timeEstimate)} · {item.milestone ? item.milestone.title : 'No milestone'}</span>
        </a>
    </div>
);

export const IssueModal = () => {
    const ticketId = useSelector(st => st.modalStatus['issueModal']);
    const [data, setData] = useState(null);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!ticketId) {
            return;
        }
        iAxios.get(`/api/v1/tickets/detail/${ticketId}`).then(resp => {
            setData(resp.data.data);
        }).catch(ex => {
            dispatch(alertMessage(ex.message, 'Error', 'ds-tone-bad'));
        });
    }, [ticketId]);

    return (
        <ModalManager modalName="issueModal" title={data?.ticket?.title || 'Issue'} className="ds-modal--wide">
            {data && (
                <div>
                    <h3>{data.ticket?.title}</h3>
                    <p style={{ color: 'var(--ds-ink-soft)', margin: 'var(--ds-space-3) 0 var(--ds-space-5)' }}>
                        {data?.ticket?.description}
                    </p>
                    <div className="ds-table-wrap">
                        <table className="ds-table">
                            <thead>
                                <tr>
                                    <th>Client</th>
                                    <th>Project</th>
                                    <th>Component</th>
                                    <th>Assignee</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{data?.ticket?.clientId}</td>
                                    <td>{data?.ticket?.projectId}</td>
                                    <td>{data?.ticket?.componentId}</td>
                                    <td>{data?.ticket?.assignee?.name}</td>
                                    <td>{data?.ticket?.state}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <h4 style={{ margin: 'var(--ds-space-6) 0 var(--ds-space-3)' }}>User Activity</h4>
                    {data?.logs?.map((i, idx) => (
                        <div className="issue-related" key={`log-${idx}`}>
                            <div>{i.data?.info}</div>
                            <div style={{ textAlign: 'right', color: 'var(--ds-ink-muted)', fontSize: 'var(--ds-text-xs)' }}>
                                {i.userId}, {moment(i.createdAt).fromNow()}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </ModalManager>
    );
};

export const IssueBlock = (props) => {
    const { item, loadLinked, labelsToRemove, user } = props;
    const [linkedIssues, setLinked] = useState([]);
    const [deleted, setDeleted] = useState(false);
    const dispatch = useDispatch();
    const projectNames = useProjectNames();

    useEffect(() => {
        if (!loadLinked) {
            return;
        }
        if (item.labels.indexOf("TaskSplit") === -1) {
            return;
        }
        const project = getIssueProject(item);
        paginatedFetch('/api/v1/tickets/related-issues', {
            project: project,
            iid: item.gitlabId
        }).then(resp => {
            setLinked(resp.data.map(r => <RelatedBlock key={r.id} item={r} />));
        });
    }, []);

    const deleteItem = (id) => {
        iAxios.post('/api/v1/tickets/delete', { id }).then(() => setDeleted(true));
    };

    const handleClick = (e) => {
        if (!e.target.href || e.target.href.indexOf('internal://') === -1) {
            return;
        }
        e.preventDefault();
        dispatch({ type: 'show-modal', name: 'issueModal', data: item.ticketId });
    };

    if (deleted) {
        return null;
    }

    const labels = item.labels.filter(l => labelsToRemove.indexOf(l) < 0);
    const project = getIssueProject(item);

    const isClosed = item.state === 'closed';
    const unestimated = item.timeEstimate === null;
    const spent = item.timeSpent || 0;
    const est = item.timeEstimate || 0;

    // Budget tracking — spent vs estimate drives the progress bar + over-budget flag.
    let overshoot = null;
    let budgetPct = 0;
    const isOver = !unestimated && est > 0 && spent > est;
    if (!unestimated && est > 0) {
        budgetPct = Math.min(100, Math.round((spent / est) * 100));
        if (isOver) {
            overshoot = Math.round(((spent / est) - 1) * 100);
        }
    }

    // Status drives the left spine colour (priority: done > over budget > unplanned > active).
    let status = 'active';
    if (isClosed) status = 'done';
    else if (isOver) status = 'over';
    else if (unestimated) status = 'unplanned';

    const cls = classNames('issue-card', `is-${status}`, { 'is-deletable': user?.role === 'Admin' });

    return (
        <div className={cls} id={item.id}>
            {item.complexity ? <span className="issue-card__complexity" title="Complexity">{item.complexity}</span> : null}
            <a
                key={"issue_" + item.id}
                className="issue-card__title"
                target="_blank"
                rel="noreferrer"
                onClick={handleClick}
                href={item.url}
            >
                {item.title}
            </a>
            {!unestimated && est > 0 && (
                <div
                    className="issue-card__budget"
                    title={`Logged ${formatSecs(spent)} of ${formatSecs(est)} estimated${overshoot !== null ? ` · over by ${overshoot}%` : ''}`}
                >
                    <span className="issue-card__budget-fill" style={{ width: `${budgetPct}%` }} />
                </div>
            )}
            <div className="issue-card__meta">
                <span><b>Spent</b> {formatSecs(item.timeSpent)}</span>
                <span><b>Est</b> {unestimated ? 'unplanned' : formatSecs(item.timeEstimate)}</span>
                <span><b>Due</b> {item.dueDate ? moment(item.dueDate).format('MMM DD') : '—'}</span>
                {overshoot !== null && <span className="issue-card__over">Over {overshoot}%</span>}
            </div>
            <div className="issue-tags">
                <span className="issue-tag is-project">{projectNames[project] || project}</span>
                {labels.map(p => <span key={p} className="issue-tag">{p}</span>)}
            </div>
            {linkedIssues}
            {user?.role === 'Admin' && (
                <button
                    type="button"
                    onClick={() => deleteItem(item.id)}
                    className="issue-card__delete"
                    aria-label="Delete issue"
                    title="Delete issue"
                >
                    <Icon name="trash" />
                </button>
            )}
        </div>
    );
};
